import { Trans, t } from "@lingui/macro";
import { WinampLogoFilled } from "@winamp/icons/WinampLogoFilled.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Form } from "react-router";
import { Header } from "~/components/Header";
import { handleLogin, requireAnonymous } from "~/modules/auth/auth.server";
import type { Route } from "./+types/auth.login";

export const meta: Route.MetaFunction = () => {
  return [{ title: t`Login - Winamp for Creators` }];
};

export async function action({ request }: Route.ActionArgs) {
  await requireAnonymous(request);

  await handleLogin(request);
}

export async function loader({ request }: Route.LoaderArgs) {
  await requireAnonymous(request);

  const url = new URL(request.url);

  if (url.searchParams.get("prompt") === "1") {
    await handleLogin(request);
  }

  return {
    error: url.searchParams.get("error"),
  };
}

export default function LoginPage({ loaderData }: Route.ComponentProps) {
  return (
    <>
      <div className="relative flex flex-col items-center text-primary font-sans antialiased w-screen min-h-screen">
        <div className="z-10 max-w-screen-xl w-full p-4">
          <Header canLogout className="bg-surface-canvas rounded-xl h-16" />
        </div>
        <main className="flex flex-1 w-full -mt-4">
          <div className="relative flex flex-1 flex-col max-w-screen-xl mx-auto p-4 md:pb-8">
            <div className="absolute inset-0 flex items-center justify-center blur-md text-white dark:text-primary/[.03]">
              <WinampLogoFilled className="size-[460px]" />
            </div>
            <div className="relative flex flex-1">
              <div className="flex flex-1 flex-col">
                <div className="flex flex-1 gap-10 lg:py-[40px] max-md:px-0 max-h-full w-full">
                  <Form
                    method="post"
                    id="form-login"
                    action="/auth/login"
                    className="flex-1 flex flex-col items-center gap-6 w-full lg:min-w-[--form-card-width-sm] max-w-[--form-card-width] m-auto md:h-fit"
                  >
                    <div className="flex flex-col items-center text-center gap-2.5">
                      <h2 className="font-bold text-2xl/7">
                        <Trans>Sign in again!</Trans>
                      </h2>
                      <p className="text-sm/5">
                        <Trans>It looks like you have been disconnected.</Trans>
                        <br />
                        <Trans> Sign in to use Winamp for Creators.</Trans>
                      </p>
                    </div>
                    {loaderData.error && (
                      <div className="text-red-500">{loaderData.error}</div>
                    )}
                    <div className="space-x-2 w-full max-w-[300px]">
                      <Button type="submit" className="w-full" size="xl">
                        <Trans>Back to login</Trans>
                      </Button>
                    </div>
                  </Form>
                </div>
                <div
                  data-unfocused
                  className="mt-auto flex items-center justify-center gap-2 py-[40px]"
                >
                  <p className="text-sm/5 text-primary">
                    <Trans>Don't have an account yet?</Trans>
                  </p>
                  <Button
                    size="2xs"
                    variant="secondary"
                    type="submit"
                    form="form-login"
                  >
                    <Trans>Sign up</Trans>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
